/**
 * I titoli di un genere, su un telefono.
 *
 * ## Perché non bastava la pagina del televisore
 *
 * `VodGenreScreen` apre con un'intestazione alta: briciole di pane, il nome del genere in caratteri
 * da oltre settanta punti, il conteggio, e tre file di comandi. Su un televisore è mezzo schermo e
 * sotto restano due righe di locandine; su un telefono è **tutto** lo schermo — «Netflix Originals»
 * va a capo, e la prima locandina si vede a metà in fondo. Si apre un genere per vedere cosa c'è
 * dentro, e non si vede niente senza scorrere.
 *
 * Qui l'intestazione è una riga sola e i comandi stanno su una striscia che scorre di lato, come le
 * categorie della guida: la griglia comincia **sopra la piega**.
 *
 * ## Tre colonne, e non due
 *
 * Due colonne darebbero locandine larghe quanto una mano, cioè tre titoli per schermata: scorrere
 * trentuno titoli diventerebbe un gesto lungo. Tre stanno in 360 punti restando riconoscibili —
 * una locandina si riconosce dalla forma e dal colore prima che dal testo.
 */

import type { ReactNode } from 'react';
import type { GenrePage, GenreSort } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

export interface PhoneGenrePageScreenProps {
  readonly page: GenrePage;
  readonly sort: GenreSort;
  readonly unwatchedOnly: boolean;
  readonly onSort: (sort: GenreSort) => void;
  readonly onToggleUnwatched: () => void;
  readonly onOpenChild: (categoryId: string) => void;
  readonly onOpen: (contentId: string) => void;
  readonly onBack: () => void;
}

export function PhoneGenrePageScreen({
  page,
  sort,
  unwatchedOnly,
  onSort,
  onToggleUnwatched,
  onOpenChild,
  onOpen,
  onBack,
}: PhoneGenrePageScreenProps): ReactNode {
  const translator = useTranslator();

  if (page.node === null) {
    return (
      <div style={{ padding: '24px 20px', color: colors.text, background: colors.background, height: '100%' }}>
        <Indietro label={translator.t('common.back')} onClick={onBack} />
        <p style={{ font: `500 15px ${FONT_STACK}`, color: colors.textMuted }}>
          {translator.t('vod.genre.gone')}
        </p>
      </div>
    );
  }

  return (
    <div
      data-phone-genre-page="1"
      style={{
        height: '100%',
        overflowY: 'auto',
        background: colors.background,
        color: colors.text,
        paddingBottom: 24,
      }}
    >
      <div style={{ padding: '14px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Indietro label={translator.t('common.back')} onClick={onBack} />

        {/*
          Nome e conteggio su **una riga**: il conteggio accanto al titolo e non sotto, perché è
          l'informazione che dice se vale la pena scorrere, e una riga in meno è una fila di
          locandine in più sopra la piega.
        */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, font: `800 min(6.5vw, 24px)/1.2 ${FONT_STACK}`, letterSpacing: '-.02em' }}>
            {page.node.name}
          </h1>
          <span style={{ font: `500 14px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t('vod.titles', { count: page.total })}
          </span>
        </div>
      </div>

      {/* I comandi: una striscia che scorre, non tre file impilate. */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          padding: '12px 20px 0',
          scrollbarWidth: 'none',
        }}
      >
        <Pastiglia
          testId="phone-genre-newest"
          label={translator.t('vod.genre.sort.newest')}
          attiva={sort === 'newest'}
          onClick={() => onSort('newest')}
        />
        <Pastiglia
          testId="phone-genre-alphabetical"
          label={translator.t('vod.genre.sort.alphabetical')}
          attiva={sort === 'alphabetical'}
          onClick={() => onSort('alphabetical')}
        />
        <Pastiglia
          testId="phone-genre-unwatched"
          label={translator.t('vod.genre.unwatchedOnly')}
          attiva={unwatchedOnly}
          onClick={onToggleUnwatched}
        />
      </div>

      {/* Le sottocategorie, quando ci sono: si entra più a fondo senza tornare all'elenco dei generi. */}
      {page.children.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            padding: '10px 20px 0',
            scrollbarWidth: 'none',
          }}
        >
          {page.children.map((figlio) => (
            <button
              key={figlio.id}
              type="button"
              data-child={figlio.id}
              onClick={() => onOpenChild(figlio.id)}
              style={{
                flex: 'none',
                minHeight: 40,
                padding: '0 14px',
                borderRadius: 20,
                border: `1px solid ${colors.borderFaint}`,
                background: colors.panel,
                color: colors.textMuted,
                font: `600 14px ${FONT_STACK}`,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {`${figlio.name} ${figlio.count}`}
              {figlio.hasChildren ? ' ›' : ''}
            </button>
          ))}
        </div>
      )}

      {page.items.length === 0 ? (
        <p style={{ padding: '24px 20px', margin: 0, font: `500 15px ${FONT_STACK}`, color: colors.textMuted }}>
          {translator.t(unwatchedOnly ? 'vod.genre.allWatched' : 'vod.genre.empty')}
        </p>
      ) : (
        <div
          data-phone-genre-grid="1"
          style={{
            display: 'grid',
            /*
              `minmax(0, 1fr)` e **non** `1fr`.

              `1fr` vale `minmax(auto, 1fr)`: il minimo è il *min-content* della cella, quindi una
              parola che non si spezza — e nel catalogo ce ne sono, perché alcuni titoli arrivano con
              dentro una riga M3U non interpretata, URL compresi — allarga la colonna oltre la
              griglia e spinge le altre fuori dallo schermo. Con `0` il minimo è zero e il contenuto
              si adatta.
            */
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 10,
            padding: '14px 20px 0',
          }}
        >
          {page.items.map((voce) => (
            <button
              key={voce.item.id}
              type="button"
              data-item={voce.item.id}
              onClick={() => onOpen(voce.item.id)}
              style={{
                padding: 0,
                border: 'none',
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: '100%',
                  aspectRatio: '2 / 3',
                  borderRadius: 10,
                  background: colors.panel,
                  overflow: 'hidden',
                  display: 'block',
                }}
              >
                {voce.item.posterUrl !== null && (
                  <img
                    src={voce.item.posterUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
              </span>
              {/*
                Il titolo su due righe al massimo, poi i puntini: senza il limite, un titolo lungo
                spinge in basso la locandina accanto e la griglia si sfalsa riga per riga.
              */}
              <span
                style={{
                  font: `600 12px/1.3 ${FONT_STACK}`,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  // Un titolo che contiene un URL non ha spazi: senza questo non va a capo mai.
                  overflowWrap: 'anywhere',
                }}
              >
                {voce.item.title}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Indietro({ label, onClick }: { readonly label: string; readonly onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      data-testid="phone-genre-back"
      onClick={onClick}
      style={{
        alignSelf: 'flex-start',
        minHeight: 40,
        padding: '0 14px',
        borderRadius: 20,
        border: `1px solid ${colors.border}`,
        background: 'transparent',
        color: colors.textMuted,
        font: `600 14px ${FONT_STACK}`,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function Pastiglia({
  label,
  attiva,
  onClick,
  testId,
}: {
  readonly label: string;
  readonly attiva: boolean;
  readonly onClick: () => void;
  readonly testId: string;
}): ReactNode {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={attiva}
      style={{
        flex: 'none',
        minHeight: 40,
        padding: '0 14px',
        borderRadius: 20,
        border: `1px solid ${attiva ? colors.accent : colors.border}`,
        background: attiva ? colors.accent : 'transparent',
        color: attiva ? colors.background : colors.textMuted,
        font: `600 14px ${FONT_STACK}`,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
